import React from "react";
import {
  Box,
  Card,
  createStyles,
  Grid,
  makeStyles,
  Typography,
  useTheme,
} from "@material-ui/core";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";

import { CustomTheme } from "../../../../types/customTheme";
import { handleImageUrl } from "../../../../utils/ChangePixelImgUrl";

const useStyles = makeStyles((theme: any) =>
  createStyles({
    imgBoxStyle: {
      margin: "7px",
      marginLeft: "10px",
      borderRadius: "10px",
      height: "120px",
      [theme.breakpoints.down("sm")]: {
        height: "92px",
      },
      display: "flex",
      alignItems: "center",
      justifyContent: "start",
    },
    titleStyle: {
      display: "-webkit-box",
      "-webkit-line-clamp": 2,
      "-webkit-box-orient": "vertical",
      overflow: "hidden",
      textOverflow: "ellipsis",
      [theme.breakpoints.down("sm")]: {
        display: "-webkit-box",
        "-webkit-line-clamp": 1,
        "-webkit-box-orient": "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
    imgBoxNotAvailableStyle: {
      marginRight: "7px",
      marginTop: "10px",
      marginLeft: "10px",
      borderRadius: "10px",
      marginBottom: "10px",
      height: "114px",
      width: "114px",
      [theme.breakpoints.down("sm")]: {
        height: "80px",
        width: "80px",
      },
      backgroundColor: theme.palette.background.imgBackground,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    img: {
      objectFit: "cover",
      width: "114px",
      height: "114px",
      [theme.breakpoints.down("sm")]: {
        width: "88px",
        height: "84px",
      },
    },
    boxStyle: {
      display: "-webkit-box",
      "-webkit-line-clamp": 3,
      "-webkit-box-orient": "vertical",
      overflow: "hidden",
      textOverflow: "ellipsis",
      textTransform: "capitalize",
    },
    /* Skeleton shown while the update request is on its way. Hand written so
       no extra package is needed. */
    "@keyframes productSkeletonPulse": {
      "0%": { opacity: 1 },
      "50%": { opacity: 0.35 },
      "100%": { opacity: 1 },
    },
    skeletonImage: {
      marginRight: "7px",
      marginTop: "10px",
      marginLeft: "10px",
      marginBottom: "10px",
      borderRadius: "10px",
      height: "114px",
      width: "114px",
      [theme.breakpoints.down("sm")]: {
        height: "80px",
        width: "80px",
      },
      backgroundColor: theme.palette.action.hover,
      animation: "$productSkeletonPulse 1.4s ease-in-out infinite",
    },
    skeletonBar: {
      backgroundColor: theme.palette.action.hover,
      borderRadius: "4px",
      animation: "$productSkeletonPulse 1.4s ease-in-out infinite",
    },
  }),
);

export interface ProductCardProps {
  item: any;
  updatingProductIds: any;
  handleSelectProduct: (event: any, item: any) => void;
}

/**
 * ProductCard Component
 *
 * A single product tile used by the 3rd Party Publish page. The card is
 * highlighted when the product is published to the 3rd party, and clicking it
 * flips that flag through the product update request.
 */
const ProductCard: React.FunctionComponent<ProductCardProps> = ({
  item,
  updatingProductIds,
  handleSelectProduct,
}) => {
  const theme: CustomTheme = useTheme();
  const classes = useStyles();
  const isPublished = !!item?.isDeliverectPublished;
  const isUpdating = updatingProductIds.includes(item?.id);

  return (
    <div
      style={{ overflow: "hidden", zIndex: 0 }}
      onClick={(event) => handleSelectProduct(event, item)}
    >
      <Card
        style={{
          overflowY: "hidden",
          cursor: isUpdating ? "progress" : "pointer",
          borderRadius: "10px",
          border: `1px solid ${theme.palette.background.entity_border}`,
          boxShadow: "none",
          zIndex: 0,
          /* The colour is not changed while the request is on its way, it is
             repainted only after the response comes back. */
          backgroundColor: isPublished
            ? theme.palette.custom.blue.main
            : theme.palette.background.entity_background,
        }}
      >
        {isUpdating ? (
          <Grid container>
            <Grid item xs={12} style={{ display: "flex" }}>
              <div className={classes.skeletonImage} />
              <Grid
                item
                xs
                container
                direction="column"
                style={{ paddingLeft: "8px", paddingTop: "14px" }}
              >
                <div
                  className={classes.skeletonBar}
                  style={{ height: "16px", width: "60%" }}
                />
                <div
                  className={classes.skeletonBar}
                  style={{ height: "12px", width: "85%", marginTop: "10px" }}
                />
                <div
                  className={classes.skeletonBar}
                  style={{ height: "12px", width: "45%", marginTop: "8px" }}
                />
              </Grid>
            </Grid>
          </Grid>
        ) : (
        <Grid container>
          <Grid item xs={12} style={{ display: "flex" }}>
            {item?.img ? (
              <Box className={classes.imgBoxStyle}>
                <img
                  className={classes.img}
                  src={handleImageUrl(item?.img)}
                  alt="not available images"
                  style={{
                    borderRadius: "10px",
                    border: `1px solid ${theme.palette.background.entity_border}`,
                  }}
                />
              </Box>
            ) : (
              <Box className={classes.imgBoxNotAvailableStyle}>
                <ImageOutlinedIcon style={{ fontSize: "52px" }} />
              </Box>
            )}
            <Grid
              item
              xs
              container
              direction="column"
              style={{ paddingLeft: "8px" }}
            >
              <Grid item xs>
                <Typography
                  style={{ fontWeight: "bold", paddingTop: "4px" }}
                  align="left"
                  className={classes.titleStyle}
                >
                  {item?.name}
                </Typography>
                <Typography
                  className={classes.boxStyle}
                  style={{ textTransform: "lowercase" }}
                  variant="body2"
                  align="left"
                >
                  {item?.desc}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        )}
      </Card>
    </div>
  );
};

export default ProductCard;
